import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, GraduationCap, Users, Gift, Sparkles, Zap, RefreshCw, Calendar, ArrowRight } from 'lucide-react';
import { Card, Button } from '../ui';

const SupportMission = () => {
  const navigate = useNavigate();

  const missionPoints = [
    {
      icon: GraduationCap,
      title: 'Center Development',
      description: 'Enhancing our facilities, equipment, and therapeutic spaces for better service delivery.'
    },
    {
      icon: Users,
      title: 'Program Innovation',
      description: 'Research and development of new wellness methodologies and treatment approaches.'
    },
    {
      icon: Heart,
      title: 'Social Initiatives',
      description: 'Periodic free events, workshops, and community wellness activities.'
    }
  ];

  const donationTypes = [
    {
      id: 'one-time',
      icon: Zap,
      title: 'One-Time Gift',
      description: 'Make a single donation',
      color: 'from-pragyavan-gold/20 to-pragyavan-gold/5',
      iconColor: 'text-pragyavan-gold',
      action: () => navigate('/donate')
    },
    {
      id: 'monthly',
      icon: RefreshCw,
      title: 'Monthly Partner',
      description: 'Become a regular supporter',
      color: 'from-sage-green/20 to-sage-green/5',
      iconColor: 'text-sage-green',
      action: () => navigate('/contact', {
        state: { prefilledMessage: 'I want to be a monthly partner' }
      })
    },
    {
      id: 'annual',
      icon: Calendar,
      title: 'Annual Supporter',
      description: 'Yearly contribution',
      color: 'from-pragyavan-brown/20 to-pragyavan-brown/5',
      iconColor: 'text-pragyavan-brown',
      action: () => navigate('/contact', {
        state: { prefilledMessage: 'I want to be an annual supporter' }
      })
    }
  ];

  return (
    <section className="py-24 bg-gradient-to-br from-pragyavan-brown to-pragyavan-earth text-pragyavan-cream relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-pragyavan-gold/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tr from-sage-green/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 mix-blend-overlay" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pragyavan-gold/10 text-pragyavan-gold text-xs font-medium mb-6 border border-pragyavan-gold/20">
              <Sparkles className="w-3 h-3" />
              <span>Our Commitment</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-display font-bold text-pragyavan-gold mb-6 leading-tight">
              Support Our <span className="text-white">Mission</span>
            </h2>
            <p className="text-lg text-pragyavan-cream/80 mb-10 leading-relaxed">
              Help us create a more peaceful world by supporting our community initiatives.
              Your contribution enables us to offer wellness programs to those who need them most.
            </p>

            {/* Mission Points */}
            <div className="space-y-8 mb-10">
              {missionPoints.map((point, index) => {
                const IconComponent = point.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="flex items-start space-x-5 group"
                  >
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:bg-pragyavan-gold/20 group-hover:border-pragyavan-gold/30 transition-all duration-300">
                        <IconComponent className="w-6 h-6 text-pragyavan-gold" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-xl text-pragyavan-gold mb-2 group-hover:text-white transition-colors duration-300">
                        {point.title}
                      </h3>
                      <p className="text-pragyavan-cream/70 text-sm leading-relaxed">
                        {point.description}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <Link to="/donate">
                <Button
                  variant="primary"
                  size="large"
                  icon={Gift}
                  iconPosition="left"
                  className="shadow-lg shadow-pragyavan-gold/20 hover:shadow-pragyavan-gold/40"
                >
                  Make a Donation
                </Button>
              </Link>
            </motion.div>
          </motion.div>

          {/* Right Column - Donation Info Card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            {/* Decorative blur behind card */}
            <div className="absolute -inset-4 bg-pragyavan-gold/20 rounded-[2rem] blur-2xl -z-10" />

            <Card
              background="cream"
              className="p-8 md:p-10 border-0 shadow-2xl relative overflow-hidden"
              rounded="large"
            >
              {/* Card Background Pattern */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-pragyavan-gold/10 to-transparent rounded-full blur-2xl pointer-events-none" />

              <Card.Body className="relative z-10">
                <div className="text-center mb-8">
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-pragyavan-gold to-accent-gold flex items-center justify-center shadow-lg shadow-pragyavan-gold/30">
                    <Heart className="w-10 h-10 text-pragyavan-brown fill-pragyavan-brown/20" />
                  </div>
                  <h3 className="text-2xl font-display font-bold text-pragyavan-brown mb-3">
                    Choose Your Way to Give
                  </h3>
                  <p className="text-warm-gray text-sm">
                    Select how you'd like to support our mission
                  </p>
                </div>

                {/* Donation Type Options */}
                <div className="space-y-4 mb-8">
                  {donationTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <motion.div
                        key={type.id}
                        whileHover={{ scale: 1.02, x: 4 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={type.action}
                        className={`cursor-pointer p-5 rounded-xl border-2 border-pragyavan-brown/10 bg-gradient-to-r ${type.color} hover:border-pragyavan-gold/50 hover:shadow-lg transition-all duration-300`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full bg-white/80 flex items-center justify-center flex-shrink-0 ${type.iconColor}`}>
                            <Icon className="w-6 h-6" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-bold text-pragyavan-brown mb-1">{type.title}</h4>
                            <p className="text-sm text-warm-gray">{type.description}</p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-pragyavan-brown flex-shrink-0" />
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Impact Statement */}
                <div className="bg-gradient-to-r from-pragyavan-gold/10 to-sage-green/10 p-6 rounded-xl border border-pragyavan-gold/10">
                  <p className="text-sm text-pragyavan-brown text-center leading-relaxed">
                    <span className="font-bold text-pragyavan-gold block mb-1 text-base">Make an Impact</span>
                    <strong>Your support</strong> helps us expand our mission and foster growth, development, and transformation in our community.
                  </p>
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SupportMission;